import { System } from '@ecs/System';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { Torpedo } from '@components/Torpedo';
import { Tag } from '@components/Tag';
import { Lifetime } from '@components/Lifetime';
import { Effects } from '@/effects/Effects';

export class TorpedoSystem extends System {
  update(_deltaTime: number): void {
    const torpedoes = this.getEntitiesWithComponents(Position, Velocity, Torpedo);
    const players = this.getEntitiesWithComponents(Position, Tag).filter(
      e => e.getComponent(Tag)!.value === 'player'
    );

    if (players.length === 0) return;

    for (const torpedo of torpedoes) {
      const position = torpedo.getComponent(Position)!;
      const velocity = torpedo.getComponent(Velocity)!;
      const torpedoComp = torpedo.getComponent(Torpedo)!;
      const lifetime = torpedo.getComponent(Lifetime);

      // Find target
      let target: Position | null = null;
      
      if (torpedoComp.targetEntityId) {
        // Try to track specific target
        const targetEntity = this.world.getEntity(torpedoComp.targetEntityId);
        if (targetEntity) {
          target = targetEntity.getComponent(Position) || null;
        }
      }
      
      // If no specific target or target lost, find nearest player
      if (!target) {
        let minDistance = Infinity;
        for (const player of players) {
          const playerPos = player.getComponent(Position)!;
          const dx = playerPos.x - position.x;
          const dy = playerPos.y - position.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < minDistance) {
            minDistance = distance;
            target = playerPos;
            torpedoComp.targetEntityId = player.id;
          }
        }
      }

      if (target) {
        // Calculate desired angle to target
        const dx = target.x - position.x;
        const dy = target.y - position.y;
        const targetAngle = Math.atan2(dy, dx);
        
        // Calculate angle difference
        let angleDiff = targetAngle - velocity.angle;
        
        // Normalize angle difference to [-PI, PI]
        while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
        while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
        
        // Apply turn rate limit
        const turnAmount = Math.max(-torpedoComp.turnRate, Math.min(torpedoComp.turnRate, angleDiff));
        velocity.angle += turnAmount;
        
        // Accelerate up to max speed
        torpedoComp.currentSpeed = Math.min(
          torpedoComp.maxSpeed,
          torpedoComp.currentSpeed + torpedoComp.acceleration
        );
        velocity.speed = torpedoComp.currentSpeed;
      }

      // Exhaust puffs every few frames
      if (lifetime && lifetime.current % 4 === 0) {
        Effects.smoke(
          this.world,
          position.x - Math.cos(velocity.angle) * 5,
          position.y - Math.sin(velocity.angle) * 5,
          1,
          '#776655'
        );
      }

      // Check if torpedo is about to expire and create explosion
      if (lifetime && lifetime.current <= 1) {
        Effects.explosion(this.world, position.x, position.y, {
          count: 12, color: '#ff8800', secondaryColor: '#ffee88', speed: 3, size: 3
        });
      }
    }
  }
}
